import { useEffect, useRef, useState } from "react";
import { Camera, Trash2, QrCode, FileDown, Eye, TrendingUp, TrendingDown, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { downloadFile } from "@/components/admin/BulkUpload";
import { API, api, formatError } from "@/lib/api";

const initials = (n) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

export function Avatar({ name, photoUrl, size = "h-20 w-20 text-2xl", testId }) {
  return photoUrl ? (
    <img data-testid={testId} src={photoUrl} alt={name} className={`${size} shrink-0 rounded-full border-2 border-white object-cover shadow-md ring-2 ring-aqua/40`} />
  ) : (
    <div data-testid={testId} className={`${size} flex shrink-0 items-center justify-center rounded-full bg-band font-bold text-white ring-2 ring-aqua/40`}>{initials(name)}</div>
  );
}

export function PhotoUpload({ user, setUser }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return toast.error("Please choose a JPG, PNG or WebP image");
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo must be 5 MB or smaller");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { setUser((await api.post("/profile/photo", fd)).data); toast.success("Photo updated"); } catch (err) { toast.error(formatError(err)); }
    setBusy(false);
  };
  const remove = async () => {
    try { setUser((await api.delete("/profile/photo")).data); toast.success("Photo removed"); } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div data-testid="photo-upload" className="flex items-center gap-4">
      <Avatar testId="profile-photo-preview" name={user.name} photoUrl={user.photo_id ? `${API}/public/photos/${user.photo_id}` : null} />
      <div className="flex flex-col gap-2">
        <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="profile-photo-input" onChange={pick} />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={busy} data-testid="profile-photo-upload-btn" onClick={() => input.current?.click()} className="rounded-full">
            <Camera className="mr-1.5 h-3.5 w-3.5" />{busy ? "Uploading..." : user.photo_id ? "Replace photo" : "Upload photo"}
          </Button>
          {user.photo_id && <Button size="sm" variant="ghost" data-testid="profile-photo-remove-btn" onClick={remove} className="rounded-full text-red-600 hover:text-red-700"><Trash2 className="mr-1.5 h-3.5 w-3.5" />Remove</Button>}
        </div>
        <p className="text-xs text-ink-3">JPG, PNG or WebP, up to 5 MB. Cropped to a square.</p>
      </div>
    </div>
  );
}

export function ShowcaseStats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/profile/showcase-stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const diff = s.this_month - s.last_month;
  const Trend = diff >= 0 ? TrendingUp : TrendingDown;
  return (
    <div className="rounded-md border border-line bg-surface">
    <div data-testid="showcase-stats" className="flex items-center gap-4 px-4 py-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-aqua-soft"><Eye className="h-5 w-5 text-aqua" /></span>
      <div className="text-sm">
        <p><b data-testid="showcase-visits-this-month" className="text-2xl text-ink">{s.this_month}</b> <span className="text-ink-2">visit{s.this_month === 1 ? "" : "s"} in {s.month_label}</span></p>
        <p data-testid="showcase-visits-last-month" className="flex items-center gap-1 text-xs text-ink-3">
          <Trend className={`h-3.5 w-3.5 ${diff >= 0 ? "text-emerald-600" : "text-amber-600"}`} />{s.last_month} in {s.last_month_label} · your own visits aren't counted
        </p>
      </div>
    </div>
    <VisitChart months={s.months} />
    </div>
  );
}

function VisitChart({ months }) {
  const max = Math.max(1, ...months.map((m) => m.count));
  return (
    <div data-testid="showcase-visit-chart" className="border-t border-line px-4 pb-3 pt-4">
      <p className="mb-3 text-xs font-medium text-ink-3">Visits over the last 6 months</p>
      <div className="flex h-28 items-end gap-3">
        {months.map((m) => (
          <div key={m.month} data-testid={`visit-bar-${m.month}`} className="group flex h-full flex-1 flex-col items-center justify-end gap-1">
            <span className={`text-[11px] font-semibold ${m.current ? "text-aqua" : "text-ink-3"}`}>{m.count}</span>
            <div title={`${m.count} visits in ${m.label}`} style={{ height: `${Math.max((m.count / max) * 100, 4)}%` }}
              className={`w-full max-w-10 origin-bottom rounded-t-md transition-[height,background-color] duration-500 ${m.current ? "bg-aqua-solid" : "bg-line group-hover:bg-line-strong"}`} />
            <span className={`text-[11px] ${m.current ? "font-bold text-ink" : "text-ink-3"}`}>{m.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function QrDownloads({ handle }) {
  return (
    <div data-testid="showcase-qr" className="flex flex-wrap items-center gap-2">
      <span className="mr-1 text-xs font-medium text-ink-2">For your CV:</span>
      <Button size="sm" variant="outline" data-testid="profile-cv-btn" className="rounded-full border-brand text-ink"
        onClick={() => downloadFile("/profile/cv", `${handle}-iddcr-cv.pdf`)}><FileText className="mr-1.5 h-3.5 w-3.5" />Download CV (PDF)</Button>
      <Button size="sm" variant="outline" data-testid="showcase-qr-png-btn" className="rounded-full"
        onClick={() => downloadFile("/profile/showcase-qr?format=png", `iddcr-showcase-${handle}.png`)}><QrCode className="mr-1.5 h-3.5 w-3.5" />Download QR (PNG)</Button>
      <Button size="sm" variant="outline" data-testid="showcase-qr-pdf-btn" className="rounded-full"
        onClick={() => downloadFile("/profile/showcase-qr?format=pdf", `iddcr-showcase-${handle}.pdf`)}><FileDown className="mr-1.5 h-3.5 w-3.5" />Printable card (PDF)</Button>
    </div>
  );
}
